import { useState } from "react";
import { TableCell, TableRow } from "@mui/material";

// COMPONENTS
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import BoxGrid from "@/main/components/shared/boxGrid/boxGrid";

// REDUX

import * as SP from "../styles";
import * as S from "./styles";
import { getTranslateByKey } from "@/main/services/utils";
import { InsurerByValuatorResponse } from "@/main/models/response/valuatorResponse";
import InsurerCompanySidebar from "./insurerCompanySidebar";
import Close from "@mui/icons-material/Close";
import LoadingComponent from "../loading/loadingComponent";
import { toast } from "react-toastify";
import { KeyValue } from "@/main/models/common";
import { valuatorApi } from "@/main/api";
import useAsyncModalConfirm from "../asyncModalConfirm/asyncModalConfirm";
import useAsyncModalConfirmReason from "../asyncModalConfirmReason/asyncModalConfirmReason";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";
import { toBase64 } from "@/main/services/utilsFile";

interface IInsurerCompanyProps {
	dataInsurerCompany: InsurerByValuatorResponse[];
	guidValuator: string;
	refreshData: () => void;
}

function InsurerCompany({ dataInsurerCompany, guidValuator, refreshData }: IInsurerCompanyProps) {
	const [open, setOpen] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();
	const [getConfirmationReason, ModalConfirmationReason] = useAsyncModalConfirmReason();

	function closeModal() {
		setOpen(false);
	}

	async function handleDeleteInsurer(insurerGuid: string, insurerName: string) {
		const message = getTranslateByKey("information.confirmRemoveValuatorInsurer").replace("{0}", insurerName);

		const isConfirmed = await getConfirmation(message);
		if (!isConfirmed) return;

		const resultConfirm = await getConfirmationReason(
			getTranslateByKey("information.confirmChangeTitle"),
			ReasonTypeEnum.ChangeCompany,
		);

		if (!resultConfirm.confirmed) return;

		let filesRequest = undefined;
		if (resultConfirm.files && resultConfirm.files.length > 0) {
			filesRequest = await Promise.all(
				resultConfirm.files.map(async (item) => {
					const base64DataUrl = await toBase64(item);
					return {
						fileName: item.name,
						contentType: item.type,
						fileBase64: base64DataUrl as string,
					};
				}),
			);
		}

		const payload = {
			guidValuatorInsurer: insurerGuid,
			changeJustification: {
				guidReason: resultConfirm.reason ? String(resultConfirm.reason.key) : null,
				reasonDescription: resultConfirm.observation || null,
				filesUpload: filesRequest,
			},
		};

		setIsLoading(true);
		valuatorApi
			.inactiveInsurerByValuator(payload)
			.then(() => {
				toast.success(getTranslateByKey("information.valuatorEditSuccessfully"));
				refreshData();
			})
			.catch((error) => {
				error.errorMessages.forEach((e: KeyValue) => {
					toast.error(e.value);
				});
			})
			.finally(() => setIsLoading(false));
	}

	return (
		<>
			<S.Container id="id-register-insurer-company">
				<SP.HeaderSection style={{ marginBottom: "0" }}>
					<SP.HeaderText>{getTranslateByKey("information.insurer")}</SP.HeaderText>
					<SecondaryButton
						id="id-register-btn-addInsurer"
						type="button"
						onClick={() => setOpen(true)}
						style={{ padding: ".2rem .8rem", margin: ".5rem 0 0 .8rem" }}
					>
						{getTranslateByKey("information.add")}
						{` ${getTranslateByKey("information.insurer").toLowerCase()}`}
					</SecondaryButton>
				</SP.HeaderSection>

				{dataInsurerCompany && dataInsurerCompany.length > 0 && (
					<BoxGrid
						rowHeaders={[getTranslateByKey("information.insurerCompany"), getTranslateByKey("information.actions")]}
						marginCustom="0.7rem 0 0 0"
					>
						{dataInsurerCompany.map((insurer) => (
							<TableRow key={insurer.guidValuator_Insurer}>
								<TableCell>{insurer.insurerCompanyName}</TableCell>
								<TableCell width="5%">
									{isLoading ? (
										<LoadingComponent size={10} />
									) : (
										<Close
											onClick={() => handleDeleteInsurer(insurer.guidValuator_Insurer, insurer.insurerCompanyName)}
											id={"id-register-icon-delete-" + insurer.guidValuator_Insurer}
											className="cursor-pointer"
										/>
									)}
								</TableCell>
							</TableRow>
						))}
					</BoxGrid>
				)}
			</S.Container>

			<InsurerCompanySidebar
				open={open}
				closeModal={closeModal}
				guidValuator={guidValuator}
				loadGridList={refreshData}
			/>
			<ModalConfirmation />
			<ModalConfirmationReason />
		</>
	);
}

export default InsurerCompany;
